"use client";

import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import * as React from "react";

import { cn } from "@/lib/utils";
import { Icon, type IconName } from "@/components/ui/icon";

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({
  asChild,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return (
    <TooltipPrimitive.Trigger
      data-slot="tooltip-trigger"
      tabIndex={-1}
      type={asChild ? undefined : "button"}
      asChild={asChild}
      {...props}
    />
  );
}
function TooltipContent({
  className,
  sideOffset = 4,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          "border-composition bg-background-dark animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-60 w-fit origin-(--radix-tooltip-content-transform-origin) rounded-md px-2 py-1 text-xs text-pretty",
          "before:bg-linear-to-t before:h-full before:w-full before:absolute before:top-0 before:left-0 overflow-hidden relative before:transition-all before:z-0 before:duration-300 before:pointer-events-none",
          "before:from-(--foreground)/20 before:dark:from-(--foreground)/5 dark:before:from-(--foreground)/10 before:to-(--foreground)/1 text-foreground",
          className,
        )}
        {...props}
      >
        {children}
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}
function TooltipMarked({
  children,
  className,
  iconStyle: _iconStyle = "solid",
  iconClassName,
  icon = "IconCircleInfo",
  ...props
}: React.PropsWithChildren &
  React.ComponentProps<typeof TooltipPrimitive.Content> & {
    iconStyle?: "stroke" | "solid";
    iconClassName?: string;
    /** The mark itself — a question mark where the tooltip answers one. */
    icon?: IconName;
  }) {
  void _iconStyle;

  return (
    <Tooltip>
      <TooltipTrigger>
        <Icon
          name={icon}
          size={16}
          className={cn("text-muted-foreground", iconClassName)}
        />
      </TooltipTrigger>
      <TooltipContent className={cn("max-w-96", className)} {...props}>
        {children}
      </TooltipContent>
    </Tooltip>
  );
}

export {
  Tooltip,
  TooltipContent,
  TooltipMarked,
  TooltipProvider,
  TooltipTrigger,
};
